<template>
  <b-card no-body>
    <b-card-body id="customers-card-body">
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'type'">
            {{ exportTypeMap[props.row.type] || props.row.type || '-' }}
          </span>

          <span v-else-if="props.column.field === 'filename'">
            {{ props.row.filename || '-' }}
          </span>

          <span v-else-if="props.column.field === 'createdAt'">
            {{ props.row.createdAt }}
          </span>

          <span v-else-if="props.column.field === 'status'">
            <b-badge :variant="statusVariant(props.row.status)">
              {{ exportStatusMap[props.row.status] || '-' }}
            </b-badge>

            <b-progress
              v-if="props.row.status === 1"
              :value="props.row.progress_percentage"
              :max="100"
              height="10px"
              class="mt-1"
            />
          </span>

          <span v-else-if="props.column.field === 'progress_percentage'">
            %{{ props.row.progress_percentage }}
          </span>

          <span v-else-if="props.column.field === 'download'">
            <b-button
              v-if="props.row.status === 2"
              size="sm"
              variant="success"
              @click="downloadFile(props.row.id, props.row.filename)"
            >
              {{ $t('Download') }}
            </b-button>
            <span v-else>-</span>
          </span>
        </template>
      </vue-good-table>

      <div v-if="!isLoading && rows.length === 0" class="text-center my-4 text-muted">
        {{ $t('Henüz dışa aktarma işlemi gerçekleştirmediniz.') }}
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BBadge, BProgress } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import { downloadBlobFile } from '@/@core/utils/utils';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BButton,
    BBadge,
    BProgress
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      exportStatus: [],
      exportType: []
    };
  },
  computed: {
    exportStatusLabel() {
      const label = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.exportStatus && this.exportStatus.length > 0) {
        label.push(
          ...this.exportStatus.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return label;
    },
    exportTypeLabel() {
      const label = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.exportType && this.exportType.length > 0) {
        label.push(
          ...this.exportType.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return label;
    },
    exportTypeMap() {
      const map = {};
      this.exportTypeLabel.forEach(item => {
        map[item.value] = item.text;
      });
      return map;
    },
    exportStatusMap() {
      const map = {};
      this.exportStatusLabel.forEach(item => {
        map[item.value] = item.text;
      });
      return map;
    },

    getColumns() {
      if (!this.rows.length) {
        return [];
      }
      return [
        { label: this.$t('Report Type'), field: 'type' },
        { label: this.$t('File Name'), field: 'filename' },
        { label: this.$t('Created At'), field: this.dateFormat },
        { label: this.$t('Status'), field: 'status' },
        { label: this.$t('Completion Percentage'), field: 'progress_percentage' },
        { label: this.$t('Download'), field: 'download' }
      ];
    }
  },
  async created() {
    this.exportType = await this.getExportTypeLabel();
    this.exportStatus = await this.getExportStatusLabel();
    this.loadItems();
  },
  methods: {
    onPageChange(params) {
      this.filter.page = params.currentPage;
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.filter.limit = params.currentPerPage;
      this.loadItems();
    },
    loadItems() {
      this.isLoading = true;

      useService
        .getExportList(this.filter)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    async downloadFile(id, filename) {
      try {
        const blob = await useService.documentExportDownload(id);

        downloadBlobFile({
          file: blob,
          fileName: filename
        });

        this.showSuccessMessage(this.$t('Download successful'), this.$t('Success'));
      } catch (error) {
        this.showErrorMessage(this.$t('Download failed'), this.$t('Error'));
      }
    },

    statusVariant(status) {
      switch (status) {
        case 0:
          return 'secondary';
        case 1:
          return 'warning';
        case 2:
          return 'success';
        case 3:
          return 'danger';
        case 4:
          return 'danger';
        default:
          return 'light';
      }
    },
    dateFormat(item) {
      return new Date(item.createdAt).toLocaleString('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short'
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.text-center {
  text-align: center;
}
</style>
